import { getConfig } from '@/config'
import { inviteQuery } from '@/utils/channel'
import { getDeviceId, getDeviceName, getDevicePlatform, detectUaPlatform } from '@/utils/device'
import { visitContext } from '@/utils/visitFlow'

export const AUTH_INVALID_EVENT = 'kp-auth-invalid'

const TOKEN_KEY = 'kp_token'

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch (e) {
    return ''
  }
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token)
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch (e) {
    /* ignore */
  }
}

function notifyAuthInvalid() {
  clearToken()
  window.dispatchEvent(new CustomEvent(AUTH_INVALID_EVENT))
}

export function ApiError(message, code) {
  const error = new Error(message || '请求失败')
  error.code = code || 400
  error.name = 'ApiError'
  return error
}

function pickMessage(json, fallback) {
  if (!json) return fallback
  return json.msg || json.message || fallback
}

async function request(path, init) {
  const { apiBase } = getConfig()
  if (apiBase == null) {
    throw ApiError('未配置接口地址', 0)
  }

  const options = init || {}
  const headers = Object.assign({ Accept: 'application/json' }, options.headers || {})
  let body = options.body

  if (options.rawBody !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(options.rawBody)
  }

  if (options.auth === 'optional') {
    const token = getToken()
    if (token) headers.Authorization = token
  } else if (options.auth) {
    const token = getToken()
    if (!token) throw ApiError('未登录', 401)
    headers.Authorization = token
  }

  const res = await fetch(`${String(apiBase).replace(/\/$/, '')}${path}`, {
    method: options.method || 'GET',
    headers,
    body,
  })

  const text = await res.text()
  let json = null
  try {
    json = text ? JSON.parse(text) : null
  } catch (e) {
    if (res.status === 401) {
      notifyAuthInvalid()
      throw ApiError('登录已失效，请重新登录', 401)
    }
    throw ApiError(text || res.statusText || '响应解析失败', res.status)
  }

  const code = (json && json.code != null) ? json.code : res.status
  if (code === 401) {
    notifyAuthInvalid()
    throw ApiError(pickMessage(json, '登录已失效，请重新登录'), 401)
  }
  if (code !== 200) {
    throw ApiError(pickMessage(json, '请求失败'), code)
  }
  return json ? json.data : undefined
}

export function pickAppUpdateUrl(info) {
  if (!info) return ''
  return String(info.downloadUrl || info.url || info.packageUrl || '').trim()
}

function uselessModel(model) {
  return !model || /^(k|mobile|android)$/i.test(model)
}

function modelFromUserAgent() {
  const matched = navigator.userAgent.match(/Android\s+[\d.]+;\s*([^;)]+?)(?:\s+Build\/|[;)])/i)
  const model = ((matched && matched[1]) || '').replace(/\s+Build$/i, '').trim()
  return uselessModel(model) ? '' : model
}

async function readDeviceModel() {
  try {
    const uaData = navigator.userAgentData
    if (uaData && uaData.getHighEntropyValues) {
      const data = await uaData.getHighEntropyValues(['model', 'platformVersion'])
      const model = String((data && data.model) || '').trim()
      if (!uselessModel(model)) return model
    }
  } catch (e) {
    /* 继续用 UA */
  }
  return modelFromUserAgent()
}

/** 点 Android 包用 CSS 像素，不要乘 DPR。 */
function reportScreen() {
  return {
    width: Math.round((window.screen && window.screen.width) || 0),
    height: Math.round((window.screen && window.screen.height) || 0),
  }
}

function applyInviteParams(query, extra) {
  const current = inviteQuery()
  const src = extra || {}
  const code = String((src.code != null && src.code !== '' ? src.code : current.code) || '').trim()
  const shareId = String((src.shareId != null && src.shareId !== '' ? src.shareId : current.shareId) || '').trim()
  const agentId = String((src.agentId != null && src.agentId !== '' ? src.agentId : current.agentId) || '').trim()
  const channel = String((src.channel != null && src.channel !== '' ? src.channel : current.channel) || '').trim()
  if (code) query.set('code', code)
  if (shareId) query.set('shareId', shareId)
  if (agentId) query.set('agentId', agentId)
  if (channel) query.set('channel', channel)
}

function deviceFields() {
  return {
    deviceId: getDeviceId(),
    devicePlatform: getDevicePlatform(),
    deviceName: getDeviceName(),
    version: getConfig().appVersion,
    clientType: 'web',
  }
}

export const api = {
  captcha() {
    return request('/api/login/captcha')
  },

  emailRegisterSendCode(email, captchaId, captchaCode) {
    return request('/api/login/emailRegister/sendCode', {
      method: 'POST',
      rawBody: {
        email,
        captchaId,
        captchaCode,
        devicePlatform: getDevicePlatform(),
        clientType: 'web',
      },
    })
  },

  emailRegister(payload) {
    return request('/api/login/emailRegister', {
      method: 'POST',
      rawBody: Object.assign({
        email: payload.email,
        password: payload.password,
        code: payload.code,
        captchaId: payload.captchaId,
        captchaCode: payload.captchaCode,
        shareId: payload.shareId || undefined,
        inviteCode: payload.shareId || undefined,
        agentCode: payload.agentCode || undefined,
      }, deviceFields()),
    })
  },

  emailLogin(account, password, captchaId, captchaCode) {
    return request('/api/login/emailLogin', {
      method: 'POST',
      rawBody: Object.assign({
        account,
        password,
        captchaId,
        captchaCode,
        type: 'email',
      }, deviceFields()),
    })
  },

  infoById() {
    const fields = deviceFields()
    const query = new URLSearchParams({
      deviceId: fields.deviceId,
      deviceName: fields.deviceName,
      devicePlatform: fields.devicePlatform,
      version: fields.version,
    })
    return request(`/api/user/infoById?${query.toString()}`, { auth: true })
  },

  signOut() {
    return request('/api/login/signOut', {
      method: 'POST',
      auth: true,
      rawBody: { deviceId: getDeviceId() },
    })
  },

  sendEmailCode(email) {
    return request('/api/email/sendCode', { method: 'POST', auth: true, rawBody: { email } })
  },

  verifyEmailCode(email, code) {
    return request('/api/email/verifyCode', { method: 'POST', auth: true, rawBody: { email, code } })
  },

  changePassword(password) {
    return request('/api/login/changePassword', { method: 'POST', auth: true, rawBody: { password } })
  },

  cancelAccount() {
    return request('/api/login/cancelAccount', { method: 'POST', auth: true, rawBody: {} })
  },

  /** 后台维护的 iOS 网页站点（获取海外 Apple ID），available 由服务器检测写入 */
  shadowrocketSites() {
    return request('/api/common/shadowrocketSites')
  },

  /** 套餐列表。建议带登录 Token。 */
  priceList() {
    return request('/api/common/price', { method: 'POST', auth: 'optional', rawBody: {} })
  },

  /** 支付方式：alipay / wechat / balance */
  payTypes() {
    return request('/api/getPayType', { auth: 'optional' })
  },

  createOrder(payload) {
    return request('/api/order/createOrder', {
      method: 'POST',
      auth: true,
      rawBody: payload,
    })
  },

  queryOrder(orderId) {
    return request('/api/order/queryOrder', {
      method: 'POST',
      auth: true,
      rawBody: { orderId },
    })
  },

  /** 网页可复制的固定订阅地址。access=free 给体验/免费领取，time/flow 给付费会员。 */
  subscribeUrl(access) {
    const kind = access === 'flow' || access === 'time' || access === 'free' ? access : 'free'
    return request(`/api/node/subscribeUrl?access=${kind}`, { auth: true })
  },

  /**
   * 未登录检查更新。优先 agentId，再 code / agentCode。
   */
  appUpdate(params) {
    const query = new URLSearchParams({
      platform: params.platform,
      version: params.version || '0.0.0',
      build: String(params.build != null ? params.build : 0),
      deviceId: getDeviceId(),
    })
    const current = inviteQuery()
    const agentId = String((params.agentId != null ? params.agentId : current.agentId) || '').trim()
    const code = String((params.code != null ? params.code : current.code) || '').trim()
    if (agentId) query.set('agentId', agentId)
    if (code) {
      query.set('code', code)
      query.set('agentCode', code)
    }
    return request(`/api/common/appUpdate?${query.toString()}`)
  },

  /**
   * 落地页安装包。
   * 进页：只带 code / shareId / agentId，不要 event、不要 platform。
   * 点下载：再带 event=download 和 platform。iOS 不调。
   * shareId 只取当前地址栏。
   */
  async inviteLanding(params) {
    const options = params || {}
    const isDownload = options.event === 'download'
    const platform = options.platform

    if (isDownload) {
      if (platform === 'ios' || detectUaPlatform() === 'ios') {
        return { downloads: {} }
      }
    }

    const query = new URLSearchParams()
    applyInviteParams(query, options)

    if (isDownload) {
      const packPlatform = (platform === 'macos' || platform === 'windows' || platform === 'android')
        ? platform
        : 'android'
      query.set('event', 'download')
      query.set('platform', packPlatform)

      const flow = visitContext()
      query.set('entry', flow.entry)
      query.set('page', flow.page)
      query.set('direct', flow.direct)
      if (flow.referrer) query.set('referrer', flow.referrer)
      query.set('stayMs', String(flow.stayMs))
      query.set('nav', flow.nav)
      query.set('visible', flow.visible)
      query.set('focus', flow.focus)
      query.set('webdriver', flow.webdriver)
      query.set('history', flow.history)
      query.set('uaPlatform', detectUaPlatform())
      query.set('deviceId', getDeviceId())

      const screenSize = reportScreen()
      if (screenSize.width) query.set('screenWidth', String(screenSize.width))
      if (screenSize.height) query.set('screenHeight', String(screenSize.height))
      const language = navigator.language || ''
      if (language) query.set('language', language)
      const timezone = (Intl.DateTimeFormat().resolvedOptions().timeZone) || ''
      if (timezone) query.set('timezone', timezone)
      const model = await readDeviceModel()
      if (model) query.set('model', model)
    }

    return request(`/api/common/invite?${query.toString()}`)
  },
}
